import * as Sentry from "@sentry/nextjs";
import { useEffect, useState } from "react";
import useSWR from "swr";

import { floorToNDecimal } from "@/features/common/helper/floorToNDecimal";
import { formatNumberToNDecimal } from "@/features/common/helper/formatNumberToNDecimal";
import { convertWeiToEther } from "@/helpers/etherConversion";
import { getPublicConfig } from "@/helpers/getPublicConfig";

import { useContract } from "./useContract";

const coinbaseRateApi =
  "https://api.coinbase.com/v2/exchange-rates?currency=ETH"; //ref:https://ethereum.stackexchange.com/questions/38309/what-are-the-popular-api-to-get-current-exchange-rates-for-ethereum-to-usd

export const useEth721Price = (tokenId: string) => {
  const { marketplaceContract } = useContract();
  const { toFixedDigits } = getPublicConfig();
  const fetcher = (url) => fetch(url).then((r) => r.json());

  const { data } = useSWR(coinbaseRateApi, fetcher, {
    revalidateIfStale: false,
  });

  const [price, setPrice] = useState<{
    price: string;
    serviceChargePercent: string;
    royaltyPercent: string;
    commissionFee: string;
    royaltyFee: string;
    totalFee: string;
    priceInEth: string;
    commissionFeeInEth: string;
    royaltyFeeInEth: string;
    totalFeeInEth: string;
    priceInUsd: string;
    formattedServiceChargePercentage: string;
    formattedRoyaltyChargePercentage: string;
  }>(undefined);

  useEffect(() => {
    if (!marketplaceContract?.methods) return;

    const getPrice = async () => {
      try {
        const result = await marketplaceContract.methods
          .getTotalPrice(tokenId)
          .call();

        const priceInEth = convertWeiToEther(result.price);

        const priceInUsd = formatNumberToNDecimal(
          (priceInEth ?? 0) * (data?.data?.rates?.USD ?? 3886.51),
          toFixedDigits,
        );

        const commissionFeeInEth = formatNumberToNDecimal(
          convertWeiToEther(result.commissionFee),
          toFixedDigits,
        );

        const royaltyFeeInEth = formatNumberToNDecimal(
          convertWeiToEther(result.royaltyFee),
          toFixedDigits,
        );

        const totalFeeInEth = formatNumberToNDecimal(
          convertWeiToEther(result.totalFee),
          toFixedDigits,
        );

        const formattedServiceChargePercentage = formatPercentage(
          Number(result.serviceChargePercent),
        );

        const formattedRoyaltyChargePercentage = formatPercentage(
          Number(result.royaltyPercent),
        );

        setPrice({
          ...result,
          priceInEth: priceInEth.toString(),
          commissionFeeInEth,
          royaltyFeeInEth,
          totalFeeInEth,
          priceInUsd,
          formattedServiceChargePercentage,
          formattedRoyaltyChargePercentage,
        });
      } catch (error) {
        Sentry.captureException(error, {
          tags: { eth721Contract: "getTotalPrice" },
          extra: { tokenId },
        });
      }
    };

    getPrice();
  }, [
    tokenId,
    marketplaceContract?.methods,
    toFixedDigits,
    data?.data?.rates?.USD,
  ]);

  return price;
};

const formatPercentage = (valueFromChain: number) => {
  return floorToNDecimal((1 / valueFromChain) * 100, 1) + "%";
};
